import {
  COUNTRY_DEFAULTS,
  SOURCE_LANGUAGE,
  isAvailable,
  type LanguageCode,
} from './registry';

export interface ResolveInput {
  /** Language segment from a public URL (/en, /ar, /es). */
  urlLanguage?: string | null;
  /** Signed-in user's saved profile language. */
  profileLanguage?: string | null;
  /** Visitor's previously chosen language (local storage). */
  storedLanguage?: string | null;
  /** Reliable country hint (ISO alpha-2), only used at unlocalized first entry. */
  country?: string | null;
  /** navigator.languages */
  browserLanguages?: readonly string[] | null;
  /** Whether review-status languages are allowed. */
  reviewEnv: boolean;
}

export type ResolveSource = 'url' | 'profile' | 'stored' | 'region' | 'browser' | 'default';

/**
 * Pure precedence chain. Unavailable (disabled) languages at any step are
 * skipped, so a disabled saved preference falls through to English without
 * the preference itself being rewritten.
 */
export function resolveLanguage(input: ResolveInput): { language: LanguageCode; source: ResolveSource } {
  const { reviewEnv } = input;
  if (isAvailable(input.urlLanguage, reviewEnv)) return { language: input.urlLanguage, source: 'url' };
  if (isAvailable(input.profileLanguage, reviewEnv)) return { language: input.profileLanguage, source: 'profile' };
  if (isAvailable(input.storedLanguage, reviewEnv)) return { language: input.storedLanguage, source: 'stored' };
  const regional = input.country ? COUNTRY_DEFAULTS[input.country.toUpperCase()] : undefined;
  if (isAvailable(regional, reviewEnv)) return { language: regional, source: 'region' };
  for (const tag of input.browserLanguages ?? []) {
    const base = tag.toLowerCase().split('-')[0];
    if (isAvailable(base, reviewEnv)) return { language: base, source: 'browser' };
  }
  return { language: SOURCE_LANGUAGE, source: 'default' };
}

/** Backend order for any message to a recipient (auth emails included). */
export function resolveRecipientLanguage(
  profileLanguage: string | null | undefined,
  signupMetadataLanguage: string | null | undefined,
  reviewEnv = false,
): LanguageCode {
  if (isAvailable(profileLanguage, reviewEnv)) return profileLanguage;
  // A saved (even disabled) profile preference always wins over stale metadata.
  if (profileLanguage) return SOURCE_LANGUAGE;
  if (isAvailable(signupMetadataLanguage, reviewEnv)) return signupMetadataLanguage;
  return SOURCE_LANGUAGE;
}
